<template>
  <div class="st-autocomplete-input">
    <label
      v-if="!labelInsideInput && label.length"
      :id="id"
      :class="'st-autocomplete-input__label-outside'"
      :for="id + 'AutocompleteCombobox'"
    >
      {{ label }}
    </label>
    <div
      :class="[
        'st-autocomplete-input-container',
        { 'st-autocomplete-input-container--invalid': invalid },
        { 'st-autocomplete-input-container--label-inside': labelInsideInput },
        { 'st-autocomplete-input-container--is-expanded': suggestionsVisible },
      ]"
    >
      <label
        v-if="labelInsideInput"
        class="st-autocomplete-input-container__label-inside"
        :id="id"
        :for="id + 'AutocompleteCombobox'"
      >
        {{ label }}
      </label>
      <div
        :class="[
          'st-autocomplete-input-container__input',
          {
            'st-autocomplete-input-container__input--has-focus': state.hasFocus,
          },
        ]"
      >
        <StInput
          aria-autocomplete="list"
          :aria-controls="id + 'AutocompleteListbox'"
          role="combobox"
          :aria-activedescendant="ariaActivedescendant"
          :aria-describedby="ariaDescribedby"
          :aria-labelledby="ariaLabelledby"
          :placeholder="placeholder"
          :id="id + 'AutocompleteCombobox'"
          :focus="onFocus"
          @blur="onBlur"
          :invalid="invalid"
          :required="required"
          v-model="inputValue"
          @update:model-value="handleUserInput"
          @keydown.esc="onEsc"
          @keydown.enter="selectFocusedItem"
          @keydown.down.prevent="onArrowKeyDown"
          @keydown.up.prevent="onArrowKeyUp"
          @keydown.delete="$emit('keydownDelete')"
          @keydown.tab="selectFocusedItem"
          :aria-expanded="suggestionsVisible ? 'true' : 'false'"
          :disabled="disabled"
        >
          <slot name="input-field-slot"></slot>
        </StInput>
      </div>
    </div>
    <ul
      class="st-autocomplete-input__suggestions"
      :id="id + 'AutocompleteListbox'"
      role="listbox"
      :aria-labelledby="id"
      v-show="suggestionsVisible"
      @mousedown.prevent
    >
      <li
        v-if="showListHeadline"
        role="presentation"
        class="st-autocomplete-input__suggestions__headline"
      >
        <slot name="list-headline"></slot>
      </li>
      <li
        v-for="(item, index) in autocompleteSuggestions"
        @click="selectItem(item)"
        @mouseover="state.hoverIndex = index"
        @mouseleave="state.hoverIndex = null"
        :key="item.id"
        role="option"
        :id="id + 'Option-' + index"
        :aria-selected="index === state.focusIndex"
        :class="[
          'st-autocomplete-input__suggestions__item',
          {
            'st-autocomplete-input__suggestions__item--focused':
              index === state.focusIndex,
          },
        ]"
      >
        <slot
          name="suggestion-item"
          :item="item"
          :index="index"
          :in-focus="index === state.focusIndex || index === state.hoverIndex"
        >
          {{ item.value }}
        </slot>
      </li>
    </ul>
  </div>
</template>

<script setup>
import StInput from "../input/Input.vue";
import { debounce } from "lodash";
import {
  defineProps,
  defineEmits,
  reactive,
  computed,
  watch,
  onMounted,
  defineModel,
} from "vue";

const props = defineProps({
  label: {
    type: String,
    default: "",
  },
  id: {
    type: String,
    default: "",
    required: true,
  },
  placeholder: {
    type: String,
    default: "",
  },
  invalid: {
    type: Boolean,
    default: false,
  },
  autocompleteSuggestions: {
    validator: (value) => {
      return value.every(
        (item) =>
          Object.prototype.hasOwnProperty.call(item, "id") &&
          Object.prototype.hasOwnProperty.call(item, "value")
      );
    },
    default: () => [],
  },
  showListHeadline: {
    type: Boolean,
  },
  labelInsideInput: {
    type: Boolean,
  },
  ariaDescribedby: {
    type: String,
  },
  ariaLabelledby: {
    type: String,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
});

const inputValue = defineModel("inputValue", { required: true });
const selectedItem = defineModel("selectedItem", { required: true });

const state = reactive({
  focusIndex: null,
  hoverIndex: null,
  showSuggestions: false,
  hasFocus: false,
});

const emit = defineEmits([
  "input",
  "esc",
  "select",
  "blur",
  "suggestionsVisible",
  "keydownDelete",
  "focus",
]);

const ariaActivedescendant = computed(() =>
  state.focusIndex !== null ? props.id + "Option-" + state.focusIndex : null
);

const suggestionsVisible = computed(
  () => state.showSuggestions && props.autocompleteSuggestions.length > 0
);

const debounceInput = debounce((val) => {
  emit("input", val);
}, 200);

const handleUserInput = (val) => {
  state.showSuggestions = true;
  debounceInput(val);
  state.focusIndex = 0;
};

const onFocus = () => {
  emit("focus");
  state.showSuggestions = true;
  state.focusIndex = null;
  state.hoverIndex = null;
  state.hasFocus = true;
};

const onArrowKeyUp = () => {
  if (state.focusIndex > 0) {
    state.focusIndex--;
  } else {
    state.focusIndex = props.autocompleteSuggestions.length - 1;
  }
};

const onArrowKeyDown = () => {
  if (
    state.focusIndex === null ||
    state.focusIndex >= props.autocompleteSuggestions.length - 1
  ) {
    state.focusIndex = 0;
  } else {
    state.focusIndex++;
  }
};

const onBlur = () => {
  state.showSuggestions = false;
  state.hasFocus = false;
  state.focusIndex = null;
  state.hoverIndex = null;
  emit("blur");
};

const selectFocusedItem = () => {
  const focusedItem = props.autocompleteSuggestions[state.focusIndex];
  if (focusedItem) {
    selectItem(focusedItem);
  }
};

const onEsc = () => {
  inputValue.value = "";
  state.showSuggestions = false;
  emit("esc");
};

const selectItem = (item) => {
  selectedItem.value = item;
  inputValue.value = item?.value || "";
  state.showSuggestions = false;
  emit("select", item);
};

onMounted(() => {
  if (selectedItem.value) {
    inputValue.value = selectedItem.value?.value || "";
  }
});

watch(suggestionsVisible, (newValue) => {
  emit("suggestionsVisible", newValue);
  if (newValue && !selectedItem.value) {
    state.focusIndex = 0;
  }
});
</script>

<style lang="scss" scoped>
@import "../../scss/main.scss";
.st-autocomplete-input {
  width: 100%;
  position: relative;

  &__label-outside {
    font-size: rem-calc(14);
    font-weight: 600;
    color: var(--text-color-primary);
    line-height: 1.4;
    display: inline-block;
    margin-bottom: rem-calc(4);
    text-align: left;
  }

  &-container {
    display: flex;
    border-radius: var(--border-radius);
    border: var(--border-width--thin) solid var(--border-color-quaternary);
    box-shadow: inset var(--border-width--thin) 0 0 0 transparent;

    &:focus-within {
      box-shadow: 0 0 0 rem-calc(2) var(--background-color-secondary),
        0 0 0 rem-calc(4) var(--border-color-quaternary);
      border-radius: var(--border-radius);
      outline: none;
    }

    &__input {
      flex-grow: 1;
    }
    &--invalid {
      border-color: var(--danger-color-primary);
    }

    :has(.st-autocomplete-input-container--invalid) &--label-inside {
      border-color: var(--danger-color-primary);
      :deep(.st-input__container) {
        border-color: var(--danger-color-primary) !important;
      }
    }

    &--is-expanded {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    :deep(.st-input__container, .st-input__container:focus-within) {
      border: none;
      box-shadow: none;
    }

    &--label-inside {
      background-color: var(--background-color-secondary);
      :deep(.st-input__container) {
        border-left: var(--border-width--thin) solid
          var(--border-color-quaternary) !important;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }

    &__label-inside {
      padding: 0 rem-calc(24);
      font-size: rem-calc(14);
      margin-bottom: 0;
      color: var(--text-color-primary);
      align-self: center;
      text-align: center;
    }
  }

  .st-autocomplete-input-container--has-focus.st-autocomplete-input-container--label-inside {
    border-left: var(--border-width--thin) solid var(--border-color-quaternary);
    box-shadow: inset var(--border-width--thin) 0 0 0
      var(--border-color-quaternary);
  }

  &__suggestions {
    font-size: rem-calc(14);
    list-style: none;
    position: absolute;
    background-color: var(--background-color-secondary);
    width: inherit;
    z-index: 3;
    margin: 0;
    padding: 0;
    border: var(--border-width--thin) solid var(--text-color-primary);
    border-bottom-left-radius: var(--border-radius);
    border-bottom-right-radius: var(--border-radius);
    overflow: hidden;
    border-top: none;
    box-sizing: border-box;

    &__headline,
    &__item {
      padding: var(--md);
      font-weight: var(--font-weight-semi-bold);
      color: var(--text-color-primary);
      &:not(:last-child) {
        border-bottom: var(--border-width--thin) solid
          var(--text-color-quaternary);
      }
    }
    &__item {
      color: var(--text-color-primary);
      &--focused,
      &:hover {
        background-color: var(--text-color-secondary);
        color: #fff;
        cursor: pointer;
      }
      &__type {
        font-size: rem-calc(14);
        font-weight: var(--font-weight-regular);
      }
    }
  }
}
</style>
